// CSS-letterbox wrapper for the dev mobile viewport preview.
// Plan: docs/planning/dev_mobile_viewport_preview.md §6.
//
// CRITICAL invariant — render the SAME React tree shape in both active and
// inactive states. Earlier this component returned `<>{children}</>` when
// inactive and a wrapping `<div><div>{children}</div></div>` when active.
// Switching between those shapes makes React unmount and remount every
// descendant — which manifested as an "app reset" the moment a dev clicked
// "Apply impersonation" with a phone preset selected. Component state for
// the entire `<App />` subtree was lost (in-flight popovers, the active
// lead workspace view, impersonation editor session state, etc.), and a
// separate symptom-fix in `useDevMode` (sessionStorage guard, dev-sync
// note `1c566b19`) only stopped dev-mode from being cleared, not the
// remount itself.
//
// Fix: always render the two-`<div>` wrapper. When inactive, both wrappers
// carry `display: contents` (no layout box, no containing block) so the
// children behave EXACTLY as if no wrapper existed. When active, the outer
// wrapper becomes the letterbox backdrop and the inner wrapper sizes to
// the phone preset. React keeps both `<div>` instances stable across the
// transition, so children only see prop/style changes — never a remount.
//
// Containing-block note: the inner phone-frame applies `transform:
// translateZ(0)` ONLY when active so any `position: fixed` descendants
// (e.g. `BugReportButton`) position relative to the frame. When inactive,
// no transform is applied — fixed descendants reference the real viewport
// as expected. `display: contents` does not establish a containing block.
//
// Controls live inside the impersonation popover
// (`DevImpersonationViewportAxis`) which itself renders INSIDE this frame —
// which is fine: the popover uses createPortal back to document.body, so it
// always paints in the real viewport regardless of where it logically sits.

import { useEffect, type CSSProperties, type ReactNode } from "react";
import { useDevViewport } from "@/hooks/useDevViewport";

const OUTER_INACTIVE_CLASS = "contents";
const OUTER_ACTIVE_CLASS =
  "fixed inset-0 z-[9000] flex items-center justify-center bg-zinc-900/95";
const INNER_INACTIVE_CLASS = "contents";
const INNER_ACTIVE_CLASS =
  "relative bg-white shadow-2xl rounded-[28px] overflow-auto ring-1 ring-zinc-700/60 dark:bg-gray-950";

export function DevViewportFrame({ children }: { children: ReactNode }) {
  const { state } = useDevViewport();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    if (state.active && state.preset && state.size) {
      html.setAttribute("data-bb-viewport", state.preset);
      html.style.setProperty("--bb-viewport-w", `${state.size.width}px`);
      html.style.setProperty("--bb-viewport-h", `${state.size.height}px`);
    } else {
      html.removeAttribute("data-bb-viewport");
      html.style.removeProperty("--bb-viewport-w");
      html.style.removeProperty("--bb-viewport-h");
    }
    return () => {
      html.removeAttribute("data-bb-viewport");
      html.style.removeProperty("--bb-viewport-w");
      html.style.removeProperty("--bb-viewport-h");
    };
  }, [state.active, state.preset, state.size]);

  const active = state.active && !!state.size;
  const innerStyle: CSSProperties | undefined =
    active && state.size
      ? {
          width: `${state.size.width}px`,
          height: `${state.size.height}px`,
          // Containing block for `position: fixed` descendants — only when
          // active. When inactive, we deliberately leave this off so fixed
          // descendants reference the real viewport (the pre-fix behavior).
          transform: "translateZ(0)",
        }
      : undefined;

  return (
    <div
      role="presentation"
      className={active ? OUTER_ACTIVE_CLASS : OUTER_INACTIVE_CLASS}
      data-bb-viewport-frame={active ? "active" : "inactive"}
    >
      <div
        className={active ? INNER_ACTIVE_CLASS : INNER_INACTIVE_CLASS}
        style={innerStyle}
        data-bb-viewport-inner
      >
        {children}
      </div>
    </div>
  );
}
